import React, { useState, useEffect } from 'react';
import PageBreadcrumbs from './PageBreadcrumbs';
import PageSideMenu from './PageSideMenu';
import PageWrapper from './PageWrapper';
import { Alert } from 'react-bootstrap';

export const rebuildCrumbs = (pages, breadcrumbs, selectedPage) => {
    const crumbs = [].concat(breadcrumbs);
    const findParent = (children, parent) => children.find(child => child.id === parent);
    let parentPage = findParent(pages, selectedPage.parent);

    if (breadcrumbs.includes(parentPage)) {
        // if the parent of selected page is already in the breadcrumbs then
        // remove all after the parent and add selected at the end
        crumbs.length = crumbs.indexOf(parentPage) + 1;
        crumbs.push(selectedPage);
    } else {
        // rebuild crumbs from scratch by adding selected page and all its
        // parents
        crumbs.length = 0;
        crumbs.push(selectedPage);
        while (parentPage) {
            crumbs.push(parentPage);
            parentPage = findParent(pages, parentPage.parent);
        }
        crumbs.reverse();
    }

    return crumbs;
};

export const pageHasMenu = menu => menu && menu.length;

/**
 * Generic UI component for switching between pages that belong to a specific main view
 *
 * Set of pages for use in this component should be defined in a configuration
 * file and then this configuration should be passed into the page switcher
 * component. See `orgPages.js` for an example of a pages configuration file.
 *
 * A standard page component for use within the page switcher with the ability
 * to navigate to other pages should be built similarly as shown in the example
 * below.
 *
 * @param {Array} pages Array of pages belonging to a specific main view
 * @param {String} wrapperSelector Custom CSS selector for the page wrapper
 * @param {String} [initialPageId] Define the initial page to start with
 *
 * @example
 *
 *  // sample of a basic page component
 *  // (component should receive the `navigateTo` function as a prop)
 *  const TestPage = ({ navigateTo }) => (
 *      <>
 *          // put your page content here
 *          <p>...</p>
 *
 *          // to add the ability to navigate to other pages add button(s)
 *          // like this to your page content
 *          <button onClick={() => navigateTo('pageTwoId')}>Page Two</button>
 *      </>
 * );
 *
 */

const PageSwitcher = ({ pages, wrapperSelector, initialPageId, ...props }) => {
    const [activeSectionId, setActiveSectionId] = useState(null);

    const pagesDefined = pages.length !== 0;
    // preset the initial page to the first page (default)...
    let initialPage = pagesDefined ? pages[0] : null;
    // ...but update it if a specific page is required and available
    if (pagesDefined && initialPageId) {
        initialPage = pages.find(page => page.id === initialPageId) || null;
    }

    const initialCrumbs = pagesDefined // build breadcrumbs based on the given pages
        ? pages.map(item => ({
            id: item.id,
            label: item.label,
            parent: item.parent,
        }))
        : [];
    const [currentPage, setCurrentPage] = useState(initialPage);
    const [breadcrumbs, setBreadcrumbs] = useState([initialCrumbs[0]]);

    useEffect(() => {
        // make sure we re-render when the set of pages is changed,
        // ex. switching between Tenant and Organization views/pages
        setCurrentPage(initialPage);
    }, [pages, initialPage]);

    const navigateToPage = (selectedPageId) => {
        const selectedPage = pages.find(page => page.id === selectedPageId);
        const crumbs = rebuildCrumbs(initialCrumbs, breadcrumbs, selectedPage);

        setBreadcrumbs(crumbs);
        setCurrentPage(crumbs[crumbs.length - 1]);
    };

    const setupBreadcrumbs = breadcrumbItems => (
        <PageBreadcrumbs items={breadcrumbItems} navigateTo={navigateToPage} />
    );

    const setupMenu = menuItems => pageHasMenu(menuItems)
        ? <PageSideMenu items={menuItems} activeSectionId={activeSectionId} />
        : null;

    // React is fussy about the capitalization of the PageComponent argument,
    // and won't render if first letter is not capitalized.
    const setupContent = PageComponent => (typeof PageComponent === 'function')
        ? PageComponent({
            navigateTo: navigateToPage,
            setActiveSectionId,
            hasMenu: pageHasMenu(currentPage.menu),
            ...props,
        })
        : (
            <PageComponent
                navigateTo={navigateToPage}
                setActiveSectionId={setActiveSectionId}
                hasMenu={pageHasMenu(currentPage.menu)}
                {...props}
            />
        );

    return (pagesDefined && initialPage)
        ? (
            <PageWrapper
                selector={wrapperSelector}
                title={currentPage.label}
                breadcrumbs={setupBreadcrumbs(breadcrumbs)}
                menu={setupMenu(currentPage.menu)}
                content={setupContent(currentPage.component)}
            />
        )
        : (
            <Alert bsStyle="warning">
                <p>
                    <strong>Warning: </strong>
                    This view has no pages.
                </p>
            </Alert>
        );
};

export default PageSwitcher;
